import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import type { StackParamList } from '../../navigation/navigation.types';
import styles from './styles.module.scss';

import mixins from '../../app/styles';
import { SocketContext } from '../../socket/socket.provider';

import { useMeQuery, useTicketsQuery } from '#/api/api.portal';
import Tooltip from '#/components/Tooltip';
import { getConfigValue } from '#/firebase/firebase';
import { useInitialRouteStore } from '#/navigation/initialRouteStore';
import { useAppNavigation } from '#/navigation/navigation.ref';
import { NOT_AUTHENTICATED_ROUTES } from '#/navigation/navigation.utils';
import { useAuthContext } from '#/screens/auth/auth.utils';
import { useSelectedBusinessUserStore } from '#/screens/home/selectedBusinessUser.store';
import { TargetPreference } from '#/socket/document.type';
import { isWhiteLabelMode } from '#/utils';
import { getChannelsWithChatAvailable } from '#/utils/serverData';
import dayjs from 'dayjs';
import { useReducer } from 'react';
import { useIsBusinessUser } from '../../hooks';
import { ROUTES } from './WebNavigationRoutes';

export type Route = {
  name: string;
  screens: Array<keyof StackParamList>;
  href: string;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  activeIfStreaming?: boolean;
  hideForBusinessUsers?: boolean;
  hideForBusiness?: boolean;
  businessIndent?: boolean;
  adminIndent?: boolean;
  showForTarget?: TargetPreference;
  showForChatsConfigured?: boolean;
  admin?: boolean;
  requireCompleteProfile?: boolean;
  showForWhiteLabel?: boolean;
  ifConfig?: string;
};

export const WebNavigation: React.FC = () => {
  const navigation = useAppNavigation();
  const isBusinessUser = useIsBusinessUser();
  const { data: profile } = useMeQuery();
  const { data: tickets = [] } = useTicketsQuery(
    { updateDate: false },
    { enabled: Boolean(profile?.lastSeenTicketsAt) },
  );
  const { webAuth, isAuth } = useAuthContext();
  const { selectedUserId } = useSelectedBusinessUserStore();
  const [mobileMenuVisible, toggleMobileMenu] = useReducer(state => !state, false);
  const {
    didLoadData,
    serverData,
    commonData: { isStreaming },
  } = useContext(SocketContext);
  const [currentRoute, setCurrentRoute] = useState<keyof StackParamList | undefined>(undefined);
  const shouldHighlightTickets =
    Boolean(profile?.lastSeenTicketsAt) &&
    tickets.some(t => dayjs(t.updatedAt).isAfter(profile?.lastSeenTicketsAt || new Date()));

  const handleNavigate = (screen: keyof StackParamList) => (e: any) => {
    e.preventDefault();

    if ((!didLoadData || !screen) && !webAuth?.admin) {
      return;
    }

    if (mobileMenuVisible) {
      toggleMobileMenu();
    }

    navigation.reset({
      routes: [
        {
          name: screen,
        },
      ],
    });
  };

  useEffect(() => {
    const saveNewRoute = () => {
      const state = navigation.getState();
      setCurrentRoute(state?.routes?.[state.index || 0]?.name || '');
    };

    navigation.addListener('state', saveNewRoute);

    saveNewRoute();
    return () => navigation.removeListener('state', saveNewRoute);
  }, [navigation]);

  const { initialRoute, setInitialRoute, initialRouteSet } = useInitialRouteStore();

  useLayoutEffect(() => {
    const route = navigation.getState()?.routes[0];

    if (!route || initialRouteSet || !navigation) {
      return;
    }

    if (!isAuth || route.name === 'Connect' || NOT_AUTHENTICATED_ROUTES.includes(route.name)) {
      setInitialRoute(undefined);
      return;
    }

    if (route.name === 'Overlays' && route.params && 'embed' in route.params) {
      // return;
    }

    if (!didLoadData && !webAuth?.admin) {
      setInitialRoute({
        name: route.name,
        params: route.params || {},
      });

      navigation.reset({ routes: [{ name: 'Connect' }] });
    }
  }, [initialRoute, setInitialRoute, initialRouteSet, isAuth, navigation, didLoadData]);

  const routesToShow = ROUTES.filter(route =>
    isWhiteLabelMode() ? route.showForWhiteLabel !== false : true,
  )
    .filter(route => (route.ifConfig ? getConfigValue(route.ifConfig) : true))
    .filter(route => (isBusinessUser ? route.hideForBusinessUsers !== true : true))
    .filter(route => (route.admin ? webAuth?.admin : true))
    .filter(route => (route.hideForBusiness ? profile?.business : true))
    .filter(route => {
      if (route.showForChatsConfigured) {
        const targetsWithChats = getChannelsWithChatAvailable(serverData);
        const hasChats = targetsWithChats.length > 0;

        if (hasChats || webAuth?.admin) {
          return true;
        }
      }

      return true;
    })
    .filter(route => {
      if (
        route.showForTarget !== undefined &&
        route.showForTarget !== serverData?.Settings?.TargetPreference &&
        !webAuth?.admin
      ) {
        return false;
      }

      return true;
    });

  return (
    <>
      <button type="button" onClick={toggleMobileMenu} className={styles.navToggle}>
        ☰
      </button>
      <div
        className={[styles.navigation, mobileMenuVisible && styles.navigationMobileVisible]
          .filter(Boolean)
          .join(' ')}
      >
        {routesToShow.map(
          ({
            name,
            screens,
            href,
            icon: Icon,
            activeIfStreaming,
            businessIndent,
            requireCompleteProfile,
          }) => {
            const externalLink = href.startsWith('http');
            const disabled =
              (!screens[0] && !externalLink) ||
              !currentRoute ||
              ['Connect', 'ThanksForSubscribing'].includes(currentRoute) ||
              (businessIndent && webAuth?.admin && !selectedUserId) ||
              (requireCompleteProfile && !profile?.complete);

            const linkStyles = [
              styles.link,
              screens.includes(currentRoute!) && styles.linkActive,
              disabled && styles.linkInactive,
              activeIfStreaming && !profile?.business && isStreaming && styles.linkGreen,
              businessIndent && (profile?.business || webAuth?.admin) && styles.indent,
              name === 'Support' && shouldHighlightTickets && styles.highlight,
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <Tooltip
                key={name}
                text={
                  requireCompleteProfile && !profile?.complete
                    ? 'Register to access this page'
                    : undefined
                }
              >
                <a
                  href={disabled ? undefined : href}
                  target={externalLink ? '_blank' : '_self'}
                  onClick={externalLink || disabled ? undefined : handleNavigate(screens[0])}
                  className={linkStyles}
                >
                  <Icon fill={mixins().color.white} />
                  <span>{name}</span>
                </a>
              </Tooltip>
            );
          },
        )}
      </div>
    </>
  );
};
